import useCompany from '@hooks/api/useCompany';
import { RootState } from '@stores/store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CUSTOM_TEMPLATE, DEFAULT_USER_ROW } from '../constants';
import { EditUserData, UserFormRow } from '../types/userDetailsForm';
import { getFilteredRoleOptions } from '../utils/utils';
import { useGetAccessTemplateTypesQuery } from '@api/userList';

// Per-row validation types

type RowTouched = Partial<Record<keyof UserFormRow, boolean>>;
type RowErrors = Partial<Record<keyof UserFormRow, string>>;

export type RowTouchedFields = RowTouched[];
export type RowFormErrors = RowErrors[];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROW_FIELD_VALIDATORS: Partial<Record<keyof UserFormRow, (value: string) => string>> = {
  email: (value) => {
    if (!value.trim()) return 'Email is required';
    if (!EMAIL_REGEX.test(value)) return 'Please enter a valid email address';
    return '';
  },
  username: (value) => (value.trim() ? '' : 'User Name is required'),
};

const VALIDATED_ROW_FIELDS = Object.keys(ROW_FIELD_VALIDATORS) as Array<keyof UserFormRow>;

const validateRowField = (field: keyof UserFormRow, value: string): string =>
  ROW_FIELD_VALIDATORS[field]?.(value) ?? '';

const makeEmptyRowTouched = (): RowTouched => ({});
const makeEmptyRowErrors = (): RowErrors => ({});

export const useUserDetailsForm = (isEditMode: boolean, userData: EditUserData | null) => {
  //user form
  const [userFormRows, setUserFormRows] = useState<UserFormRow[]>([{ ...DEFAULT_USER_ROW }]);
  const [rowTouchedFields, setRowTouchedFields] = useState<RowTouchedFields>([
    makeEmptyRowTouched(),
  ]);
  const [rowFormErrors, setRowFormErrors] = useState<RowFormErrors>([makeEmptyRowErrors()]);

  //user template
  const [internalUserAccessTemplateType, setInternalUserAccessTemplateType] = useState<string>(
    CUSTOM_TEMPLATE.internalUserAccessTemplateType,
  );
  const { data: templates, isLoading: isLoadingTemplates } = useGetAccessTemplateTypesQuery();

  //Company
  const { getCompanyList } = useCompany();
  const companies = useSelector((state: RootState) => state.CompanyListData);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  useEffect(() => {
    if (!companies.companyList.length) {
      getCompanyList();
    }
  }, []);

  // Pre-fill user details only in edit mode (clone starts with blank user fields)
  useEffect(() => {
    if (!isEditMode || !userData) return;
    setUserFormRows([
      { email: userData.email, username: userData.username, userRole: userData.userRole },
    ]);
    setSelectedCompanyId(userData.companyId);
    setInternalUserAccessTemplateType(
      userData.internalUserAccessTemplateType ?? CUSTOM_TEMPLATE.internalUserAccessTemplateType,
    );
    setRowTouchedFields([makeEmptyRowTouched()]);
    setRowFormErrors([makeEmptyRowErrors()]);
  }, [isEditMode, userData]);

  // Handlers
  const handleFieldChange = useCallback(
    (index: number, field: keyof UserFormRow, value: string) => {
      setUserFormRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

      // Re-validate on change only if the field has already been touched
      if (rowTouchedFields[index]?.[field]) {
        setRowFormErrors((prev) => {
          const next = [...prev];
          next[index] = { ...next[index], [field]: validateRowField(field, value) };
          return next;
        });
      }
    },
    [rowTouchedFields],
  );

  const handleFieldBlur = useCallback(
    (index: number, field: keyof UserFormRow) => {
      setRowTouchedFields((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: true };
        return next;
      });

      setRowFormErrors((prev) => {
        const next = [...prev];
        const value = (userFormRows[index]?.[field] as string) ?? '';
        next[index] = { ...next[index], [field]: validateRowField(field, value) };
        return next;
      });
    },
    [userFormRows],
  );

  const handleAddUserFormRow = useCallback(() => {
    setUserFormRows((prev) => [...prev, { ...DEFAULT_USER_ROW }]);
    setRowTouchedFields((prev) => [...prev, makeEmptyRowTouched()]);
    setRowFormErrors((prev) => [...prev, makeEmptyRowErrors()]);
  }, []);

  const handleRemoveUserFormRow = useCallback((index: number) => {
    setUserFormRows((prev) => prev.filter((_, i) => i !== index));
    setRowTouchedFields((prev) => prev.filter((_, i) => i !== index));
    setRowFormErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const getRoleOptions = useCallback(
    (email: string) => {
      return getFilteredRoleOptions(email, selectedCompanyId, companies.companyList);
    },
    [selectedCompanyId, companies.companyList],
  );

  /**
   * Marks all validated fields in all rows as touched and runs full validation.
   * Returns true if every row is valid — use this before submission.
   */
  const validateAll = useCallback((): boolean => {
    const allTouched: RowTouchedFields = userFormRows.map(() =>
      VALIDATED_ROW_FIELDS.reduce<RowTouched>((acc, field) => ({ ...acc, [field]: true }), {}),
    );
    setRowTouchedFields(allTouched);

    const allErrors: RowFormErrors = userFormRows.map((row) =>
      VALIDATED_ROW_FIELDS.reduce<RowErrors>(
        (acc, field) => ({ ...acc, [field]: validateRowField(field, row[field] as string) }),
        {},
      ),
    );
    setRowFormErrors(allErrors);

    return allErrors.every((rowErrors) => Object.values(rowErrors).every((err) => !err));
  }, [userFormRows]);

  // Derived state
  const areAllRowsValid =
    userFormRows.every((r) => r.email.trim() !== '' && r.username.trim() !== '') &&
    selectedCompanyId !== '';

  const templateMenuItems = useMemo(
    () =>
      [...(templates?.data ?? []), CUSTOM_TEMPLATE].map((t) => ({
        value: t.internalUserAccessTemplateType,
        label: t.label,
      })),
    [templates?.data],
  );

  const templateSubmodules = useMemo(() => {
    return templates?.data.find(
      (t) => t.internalUserAccessTemplateType === internalUserAccessTemplateType,
    )?.submodules;
  }, [templates?.data, internalUserAccessTemplateType]);

  return {
    // Company
    selectedCompanyId,
    setSelectedCompanyId,
    companies,

    // User form
    areAllRowsValid,
    userFormRows,
    setUserFormRows,
    rowTouchedFields,
    rowFormErrors,
    handleFieldChange,
    handleFieldBlur,
    handleAddUserFormRow,
    handleRemoveUserFormRow,
    getRoleOptions,
    validateAll,

    // User template
    templateMenuItems,
    isLoadingTemplates,
    internalUserAccessTemplateType,
    templateSubmodules,
    setInternalUserAccessTemplateType,
  };
};
